import React, {
  useContext,
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
  createContext
} from 'react';

export type FormDialogErrors = {
  noteExceeded: boolean;
  commentExceeded: boolean;
};

type Variant = 'note' | 'splitNote';
type FormDialogErrorsContextType = {
  errorContext: FormDialogErrors;
  setErrorContext: Dispatch<SetStateAction<FormDialogErrors>>;
  variant: Variant;
};

export const getDefaultFilterContext = (): FormDialogErrors => ({
  noteExceeded: false,
  commentExceeded: false
});

export const FormErrorDialogContext = createContext<FormDialogErrorsContextType>(
  {
    errorContext: getDefaultFilterContext(),
    setErrorContext: () => {},
    variant: 'note'
  }
);

export const useFormErrorDialogContext = (): FormDialogErrorsContextType => {
  const val = useContext(FormErrorDialogContext);
  if (!val) {
    throw new Error(
      'useFormErrorDialogContext must be called within a FormErrorDialogContextProvider'
    );
  }

  return val;
};

type FormErrorDialogContextProviderProps = {
  children: React.ReactNode;
  variant?: Variant;
};

export const FormErrorDialogContextProvider: React.FC<FormErrorDialogContextProviderProps> = ({
  variant = 'note',
  children
}: FormErrorDialogContextProviderProps) => {
  const [errorContext, setErrorContext] = useState(getDefaultFilterContext());

  const value = useMemo(() => ({ errorContext, setErrorContext, variant }), [
    errorContext,
    setErrorContext,
    variant
  ]);
  return (
    <FormErrorDialogContext.Provider value={value}>
      {children}
    </FormErrorDialogContext.Provider>
  );
};
